@file:OptIn(ExperimentalMaterial3Api::class)

package us.laantech.admin.menu.portfolio

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import us.laantech.common.dto.InternalProductDTO
import us.laantech.common.ui.DataTableView
import us.laantech.common.dto.InventoryDTO
import us.laantech.common.util.centsToDollarString
import us.laantech.common.util.dollarStringToCents
import us.laantech.common.api.KtorClient
import kotlin.time.Clock

data class ProductPortfolioUiState(
    val products: List<InternalProductDTO> = emptyList(),

    val selectedProduct: InternalProductDTO? = null,

    // Create Product Modal
    val showCreateProductModal: Boolean = false,
    val productNameTextField: TextFieldState = TextFieldState(""),
    val websiteTextField: TextFieldState = TextFieldState(""),
    val brandTextField: TextFieldState = TextFieldState(""),
    val skuTextField: TextFieldState = TextFieldState(""),
    val descriptionTextField: TextFieldState = TextFieldState(""),

    // Update Inventory Modal
    val showUpdateInventoryModal: Boolean = false,
    val addedCountTextField: TextFieldState = TextFieldState(""),
    val unitCostTextField: TextFieldState = TextFieldState(""),
    val soldCountTextField: TextFieldState = TextFieldState(""),
    val expiredCountTextField: TextFieldState = TextFieldState(""),
    val damagedCountTextField: TextFieldState = TextFieldState(""),
    val writeOffCountTextField: TextFieldState = TextFieldState(""),
    val storeUseCountTextField: TextFieldState = TextFieldState(""),
    val lostCountTextField: TextFieldState = TextFieldState(""),

)


@Composable
fun ProductPortfolioView(
    viewModel: ProductPortfolioViewModel = viewModel(
        factory = viewModelFactory {
            initializer { ProductPortfolioViewModel() }
        }
    )
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    LaunchedEffect(Unit) {
        viewModel.loadProducts()
    }


    Column {
        Text("Product Portfolio")

        Button(viewModel::toggleCreateProductModal) { Text("Create Product") }

        DataTableView(
            modifier = Modifier.fillMaxSize(),
            rows = uiState.products,
        ) {
            column("Product UUID") {
                Text(it.uuid ?: "", modifier = Modifier.weight(1f))
            }
            column("Product Name") {
                Text("${it.productName}", modifier = Modifier.weight(1f))
            }
            column("Inventory", weight = 0.5f) {
                Text("${it.inventory.total}", modifier = Modifier.weight(0.5f))
            }
            column("Unit Cost", weight = 0.5f) {
                Text("${it.inventory.unitCostCents?.centsToDollarString()}", modifier = Modifier.weight(0.5f))
            }
            column("Actions") {
                Row(Modifier.weight(1f)) {
                    Button({ viewModel.toggleUpdateInventoryModal(it) }) { Text("Update") }
                }
            }
        }
    }



    if (uiState.showCreateProductModal) {
        CreateProductModal(viewModel)
    }

    if (uiState.showUpdateInventoryModal) {
        UpdateInventoryModal(viewModel)
    }

}

@Composable
fun CreateProductModal(viewModel: ProductPortfolioViewModel) {

    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    BasicAlertDialog(viewModel::toggleCreateProductModal) {
//        Surface
        Column {
            Text("Create Product")
            TextField(uiState.productNameTextField, label = { Text("Product Name") })
            TextField(uiState.websiteTextField, label = { Text("Website") })
            TextField(uiState.brandTextField, label = { Text("Brand") })
            TextField(uiState.skuTextField, label = { Text("SKU") })
            TextField(uiState.descriptionTextField, label = { Text("Description") })

            Button(viewModel::createProduct) { Text("Create") }
        }
    }
}


@Composable
fun UpdateInventoryModal(viewModel: ProductPortfolioViewModel) {

    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    BasicAlertDialog(viewModel::toggleUpdateInventoryModal) {
//        Surface
        Column {
            Text("Update Inventory")
            TextField(uiState.addedCountTextField, label = { Text("Added Count") })
            TextField(uiState.unitCostTextField, label = { Text("Unit Cost") })
            TextField(uiState.soldCountTextField, label = { Text("Sold Count") })
            TextField(uiState.expiredCountTextField, label = { Text("Expired Count") })
            TextField(uiState.damagedCountTextField, label = { Text("Damaged Count") })
            TextField(uiState.writeOffCountTextField, label = { Text("Write Off Count") })
            TextField(uiState.storeUseCountTextField, label = { Text("Store Use Count") })
            TextField(uiState.lostCountTextField, label = { Text("Lost Count") })

            Button(viewModel::updateInventory) { Text("Create") }
        }
    }
}


class ProductPortfolioViewModel: ViewModel() {

    private val _uiState = MutableStateFlow(ProductPortfolioUiState())
    val uiState = _uiState.asStateFlow()
    val ktClient = KtorClient.instance.client
    val scope = CoroutineScope(Dispatchers.Default)

    fun loadProducts() {
        scope.launch {
            val response = ktClient.get("/admin/internal-products/list")
            val products = response.body<List<InternalProductDTO>>()
            _uiState.update { it.copy(products = products) }
        }
    }

    fun toggleCreateProductModal() {
        _uiState.update {
            it.copy(
                showCreateProductModal = !it.showCreateProductModal,
                // Reset fields
                productNameTextField = TextFieldState(""),
                websiteTextField = TextFieldState(""),
                brandTextField = TextFieldState(""),
                skuTextField = TextFieldState(""),
                descriptionTextField = TextFieldState(""),
                )
        }
    }

    fun createProduct() {
        scope.launch {

            val body = InternalProductDTO(
                productName = uiState.value.productNameTextField.text.toString(),
                website = uiState.value.websiteTextField.text.toString(),
                brand = uiState.value.brandTextField.text.toString(),
                sku = uiState.value.skuTextField.text.toString(),
                description = uiState.value.descriptionTextField.text.toString()
            )

            val response = ktClient.post("/admin/internal-products") {
                contentType(ContentType.Application.Json)
                setBody(body)
            }

            val result = response.body<InternalProductDTO>()
            _uiState.update { it.copy(products = it.products + result) }

            toggleCreateProductModal()
        }
    }

    fun toggleUpdateInventoryModal() {
        _uiState.update {
            it.copy(
                showUpdateInventoryModal = !it.showUpdateInventoryModal,
                // Reset fields
                addedCountTextField = TextFieldState(""),
                unitCostTextField = TextFieldState(""),
                soldCountTextField = TextFieldState(""),
                expiredCountTextField = TextFieldState(""),
                damagedCountTextField = TextFieldState(""),
                writeOffCountTextField = TextFieldState(""),
                storeUseCountTextField = TextFieldState(""),
                lostCountTextField = TextFieldState(""),
            )
        }
    }

    fun toggleUpdateInventoryModal(product: InternalProductDTO) {
        _uiState.update {
            it.copy(selectedProduct = product)
        }
        toggleUpdateInventoryModal()
    }

    fun updateInventory() {

        val unitCostCents = uiState.value.unitCostTextField.text.toString().dollarStringToCents()

        val inventory = InventoryDTO(
            createdAt = Clock.System.now().toEpochMilliseconds(),
            addedCount = uiState.value.addedCountTextField.text.toString().toLongOrNull(),
            unitCostCents = unitCostCents,
            soldCount = uiState.value.soldCountTextField.text.toString().toLongOrNull(),
            expiredCount = uiState.value.expiredCountTextField.text.toString().toLongOrNull(),
            damagedCount = uiState.value.damagedCountTextField.text.toString().toLongOrNull(),
            writeOffCount = uiState.value.writeOffCountTextField.text.toString().toLongOrNull(),
            storeUseCount = uiState.value.storeUseCountTextField.text.toString().toLongOrNull(),
            lostCount = uiState.value.lostCountTextField.text.toString().toLongOrNull(),
        )

        scope.launch {
            val response = ktClient.patch("/admin/internal-products/inventory") {
                contentType(ContentType.Application.Json)
                parameter("uuid", uiState.value.selectedProduct?.uuid)
                setBody(inventory)
            }

            if (response.status == HttpStatusCode.OK) {
                _uiState.update { it.copy(showUpdateInventoryModal = false) }
            } else {
                println("Error updating inventory: ${response.status}")
            }

        }
    }


}